import React, { useEffect, useState } from 'react';
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Box,
} from '@mui/material';
import { IconChevronDown16, IconGeneral } from '@shared/assets/images/svgs';
import CustomAlert from '@shared/components/alert/alert';
import { CampaignRetryFields } from '@shared/components/campaignRetryFields/CampaignRetryFields';
import CustomFormLabelV2 from '@shared/components/forms/theme-elements/CustomFormLabelV2';
import { CounterInput } from '@shared/components/counterInput/counterInput';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { ChangeRetryValueByButtonData } from '@shared/components/campaignRetryFields/CampaignRetryFields';
import { initialSchedule } from '@shared/constants/initialSchedule';
import { CallScheduleRequestDto, CampaignDto } from '@shared/services/apiService/apiService';
import { CampaignFieldsError } from '@shared/types/admin/campaign';
import { ScheduleSelect } from '@widgets/Popup/Schedule/ScheduleSelect';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { StyledErrorText } from '@shared/components/errorText';
import { CreateCampaignFormValue } from '@features/Admin/Campaign/model/type';
import { CampaignTypeEnum } from '@shared/constants/campaign.constants';
import { RootState, useAppSelector } from '@shared/store';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { clampReservedLimit } from './reservedLimit.utils';

interface GeneralTabProps {
  campaign?: CampaignDto | null;
  actions?: React.ReactNode;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ campaign, actions }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    control,
    formState: { errors: formStateErrors },
  } = useFormContext<CreateCampaignFormValue>();
  const currentUser = useAppSelector((state: RootState) => state.auth.authenticatedUser);

  const [campaignFieldError, setCampaignFieldError] = useState<CampaignFieldsError>({
    retryIntervalInMinutes: [],
    retryMaxNumber: [],
    retriesPerDay: [],
    editErrorMessage: '',
  });

  const [schedule, setSchedule] = useState<CallScheduleRequestDto>(initialSchedule);
  const [retryMaxNumberValue, setRetryMaxNumberValue] = useState('');
  const [retriesPerDayValue, setRetriesPerDayValue] = useState('');
  const [retryIntervalInMinutesValue, setRetryIntervalInMinutesValue] = useState('');

  useEffect(() => {
    if (campaign && campaign.callSchedule) {
      setRetryMaxNumberValue(campaign.retryMaxNumber?.toString() || '0');
      setRetriesPerDayValue(campaign.retriesPerDay?.toString() || '0');
      setRetryIntervalInMinutesValue(campaign.retryIntervalInMinutes?.toString() || '0');
      setSchedule(campaign.callSchedule);
    }
  }, [campaign]);

  const setCallSchedule = (
    value: CallScheduleRequestDto,
    onChange: (value: CallScheduleRequestDto) => void,
  ) => {
    setSchedule(value);
    onChange(value);
  };

  let fields = [
    {
      name: 'retryMaxNumber',
      label: t('campaignPage.overallRetryAttempt'),
      placeholder: t('campaignPage.overallRetryAttemptPlaceholder'),
      retryFieldError: campaignFieldError.retryMaxNumber || [],
      retryFieldValue: retryMaxNumberValue,
      setRetryFieldValue: setRetryMaxNumberValue,
    },
    {
      name: 'retriesPerDay',
      label: t('campaignPage.retryPerDay'),
      placeholder: t('campaignPage.retryPerDayPlaceholder'),
      retryFieldError: campaignFieldError.retriesPerDay || [],
      retryFieldValue: retriesPerDayValue,
      setRetryFieldValue: setRetriesPerDayValue,
    },
    {
      name: 'retryIntervalInMinutes',
      label: t('campaignPage.retryDelay'),
      placeholder: t('campaignPage.retryDelayPlaceholder'),
      retryFieldError: campaignFieldError.retryIntervalInMinutes || [],
      retryFieldValue: retryIntervalInMinutesValue,
      setRetryFieldValue: setRetryIntervalInMinutesValue,
    },
  ];

  return (
    <>
      <Box component="form" aria-label={t('campaignPage.general')} sx={{ width: '100%' }}>
        <Box
          sx={{
            px: { xs: 2, sm: 3, md: 4 },
            py: { xs: 2, sm: 3 },
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: 1100,
            display: 'grid',
            gap: 3,
            minWidth: 0,
          }}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              minHeight: 36,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 36,
                'svg path': {
                  stroke: theme.palette.getContrastText(theme.palette.background.default),
                },
              }}>
              <IconGeneral />
              <Typography component="h2" variant="h5">
                {t('campaignPage.general')}
              </Typography>
            </Box>
            {actions}
          </Box>
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              border: 1,
              borderColor: 'border.default',
              borderRadius: 1,
              bgcolor: 'surface.card',
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 240px' },
              gap: 3,
              alignItems: 'end',
            }}>
            <Box sx={{ minWidth: 0 }}>
              <CustomFormLabelV2
                sx={{
                  mt: 0,
                }}
                htmlFor="campaign-title"
                required>
                {t('campaignPage.campaignName')}
              </CustomFormLabelV2>
              <Controller
                control={control}
                name="title"
                disabled={currentUser?.role === UserRolesEnum.VIEWER}
                rules={{ required: true }}
                render={({ field }) => (
                  <CustomTextField
                    {...field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      field.onChange(e.target.value);
                    }}
                    id="campaign-title"
                    data-testid="campaign-title"
                    fullWidth
                    size="small"
                    sx={{
                      ...(formStateErrors.title
                        ? {
                            '& fieldset': {
                              borderColor: `${theme.palette.error.main} !important`,
                            },
                          }
                        : {}),
                    }}
                    variant="outlined"
                    value={field.value}
                    required
                  />
                )}
              />
              {formStateErrors.title && (
                <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
              )}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <CustomFormLabelV2 sx={{ mt: 0 }} htmlFor="campaign-reserved-limit">
                {t('campaignPage.reservedLimit')}
              </CustomFormLabelV2>
              <Box sx={{ maxWidth: { xs: 280, lg: 'none' } }}>
                <Controller
                  control={control}
                  name="reservedLimit"
                  disabled={currentUser?.role === UserRolesEnum.VIEWER}
                  rules={{ min: 1 }}
                  render={({ field }) => (
                    <CounterInput
                      id="campaign-reserved-limit"
                      retryFieldValue={
                        field.value === undefined || field.value === null ? '' : String(field.value)
                      }
                      fieldName="reservedLimit"
                      control={control}
                      isErrored={Boolean(formStateErrors.reservedLimit)}
                      min={1}
                      handleOnChangeRetryFieldInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const filteredValue = e.target.value.replace(/[^0-9]/g, '');
                        if (filteredValue === '') {
                          field.onChange(undefined);
                          return;
                        }
                        field.onChange(Math.max(1, parseInt(filteredValue, 10)));
                      }}
                      handleOnChangeRetryValueByButton={(data: ChangeRetryValueByButtonData) =>
                        field.onChange(clampReservedLimit(data.value))
                      }
                    />
                  )}
                />
              </Box>
              {formStateErrors.reservedLimit && (
                <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
              )}
            </Box>
          </Box>
          {campaign?.type === CampaignTypeEnum.OUTBOUND && (
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                border: 1,
                borderColor: 'border.default',
                borderRadius: 1,
                bgcolor: 'surface.card',
                minWidth: 0,
                '& .MuiGrid-container': { alignItems: 'flex-end' },
              }}>
              <CampaignRetryFields fields={fields} setCampaignFieldError={setCampaignFieldError} />
            </Box>
          )}
          {campaign?.type === CampaignTypeEnum.OUTBOUND && (
            <Accordion
              defaultExpanded
              sx={{
                boxShadow: 'none',
                border: 1,
                borderColor: 'border.default',
                borderRadius: 1,
                bgcolor: 'surface.card',
                minWidth: 0,
                '&.Mui-expanded': { m: 0 },
                '&::before': { display: 'none' },
              }}>
              <AccordionSummary
                expandIcon={<IconChevronDown16 />}
                aria-controls="panel1-content"
                id="panel1-header"
                sx={{
                  px: { xs: 2, sm: 3 },
                  py: 0.5,
                  minHeight: 56,
                  '&.Mui-expanded': { minHeight: 56 },
                  '& .MuiAccordionSummary-content': {
                    my: 1.5,
                    '&.Mui-expanded': { my: 1.5 },
                  },
                  'svg path': {
                    stroke: theme.palette.primary.main,
                  },
                }}>
                <Typography component="span" variant="subtitle1" fontWeight={600}>
                  {t('campaignPage.changeSchedule')}
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: { xs: 2, sm: 3 },
                  pb: { xs: 2, sm: 3 },
                  pt: 0,
                  minWidth: 0,
                  '& .MuiTable-root': { minWidth: 560 },
                }}>
                <Controller
                  control={control}
                  name="callSchedule"
                  render={({ field }) => (
                    <ScheduleSelect
                      schedule={field.value ?? schedule}
                      setSchedule={(value) => setCallSchedule(value, field.onChange)}
                    />
                  )}
                />
                {formStateErrors.callSchedule && (
                  <StyledErrorText>{t('adminMainPage.errors.fillTheField')}</StyledErrorText>
                )}
              </AccordionDetails>
            </Accordion>
          )}
        </Box>
      </Box>
      {campaignFieldError.editErrorMessage ? (
        <CustomAlert message={campaignFieldError.editErrorMessage} type={'error'} />
      ) : (
        ''
      )}
    </>
  );
};

export default GeneralTab;
